/**
 * MeasurementDeltaChart — renders a simple timeline of lesion measurements for
 * the progression-since-treatment workflow. It shows each measurement as a
 * bar scaled to the largest dimension, with dates and percent changes.
 *
 * This is intentionally dependency-free; it uses MUI Box for the bars.
 */
import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import { typography } from '~/theme';

export interface TimelineMeasurement {
  lesion_id: string;
  dimension_mm: number;
  study_date: string;
  location?: string | null;
}

interface MeasurementDeltaChartProps {
  measurements: TimelineMeasurement[];
}

const MeasurementDeltaChart: React.FC<MeasurementDeltaChartProps> = ({ measurements }) => {
  if (!measurements || measurements.length === 0) return null;

  const sorted = [...measurements].sort(
    (a, b) => new Date(a.study_date).getTime() - new Date(b.study_date).getTime()
  );
  const maxDimension = Math.max(...sorted.map(m => m.dimension_mm));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const absoluteChange = last.dimension_mm - first.dimension_mm;
  const percentChange = first.dimension_mm
    ? (absoluteChange / first.dimension_mm) * 100
    : 0;

  const trendColor =
    percentChange > 20 ? 'error' : percentChange < -30 ? 'success' : 'warning';

  return (
    <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={typography.label.fontWeight}>
          Measurement Timeline
        </Typography>
        <Chip
          label={`${absoluteChange >= 0 ? '+' : ''}${absoluteChange.toFixed(1)} mm (${percentChange >= 0 ? '+' : ''}${percentChange.toFixed(1)}%)`}
          size="small"
          color={trendColor}
          sx={{ height: 18, fontSize: '0.65rem' }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {sorted.map((m, i) => {
          const widthPct = maxDimension ? (m.dimension_mm / maxDimension) * 100 : 0;
          return (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 80, textAlign: 'right' }}
              >
                {m.study_date}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: 16,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${Math.max(widthPct, 4)}%`,
                    height: '100%',
                    bgcolor:
                      i === 0
                        ? 'primary.light'
                        : m.dimension_mm >= first.dimension_mm
                          ? 'error.light'
                          : 'success.light',
                    transition: 'width 0.5s ease',
                  }}
                />
              </Box>
              <Typography variant="caption" fontWeight={typography.label.fontWeight} sx={{ minWidth: 50 }}>
                {m.dimension_mm.toFixed(1)} mm
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default MeasurementDeltaChart;
